import { useMemo } from 'react'
import { CofidisMonthlyApplicationFormValues } from './types'
import { useCustomer } from 'hooks/useSession'
import type { CofidisTitleCode } from './enums'

/** Map customer title to Cofidis title code: '1' = Sr, '2' = Sra */
function mapCustomerTitleToCofidis(
  title: string | null | undefined
): CofidisTitleCode | undefined {
  if (!title?.trim()) return undefined
  const t = title.trim().toLowerCase()
  if (['male', 'mr', 'mister', 'sr', 'señor', 'senor', '1'].includes(t))
    return '1'
  if (['female', 'mrs', 'ms', 'sra', 'señora', 'senora', '2'].includes(t))
    return '2'
  return undefined
}

/** Split Spanish-style double surname; Cofidis expects lastName1 + lastName2. */
function splitLastNames(lastName: string | null | undefined): {
  lastName1: string
  lastName2: string
} {
  const full = (lastName ?? '').trim()
  if (!full) return { lastName1: '', lastName2: '' }
  const idx = full.indexOf(' ')
  if (idx === -1) return { lastName1: full, lastName2: '' }
  return {
    lastName1: full.slice(0, idx),
    lastName2: full.slice(idx + 1).trim(),
  }
}

/** Parse YYYY-MM-DD or ISO date string to Date; return undefined if invalid. */
function parseBirthDate(value: string | null | undefined): Date | undefined {
  if (!value?.trim()) return undefined
  const d = new Date(value.trim())
  return Number.isNaN(d.getTime()) ? undefined : d
}

/**
 * Builds partial Cofidis form defaults from the current customer.
 * Only includes fields we can meaningfully derive; form defaults (e.g. editionMode) stay in useCofidisApplicationForm.
 */
const useCofidisApplicationFormDefaults = ():
  | Partial<CofidisMonthlyApplicationFormValues>
  | undefined => {
  const customer = useCustomer()

  return useMemo(() => {
    if (!customer) return undefined

    const { lastName1, lastName2 } = splitLastNames(customer.last_name)
    const birthDate = parseBirthDate(customer.birth_date)
    const address = customer.address

    const defaults: Partial<CofidisMonthlyApplicationFormValues> = {}

    // --- Identity & contact (customer fields map directly) ---
    const title = mapCustomerTitleToCofidis(customer.title)
    if (title !== undefined) defaults.title = title

    if (customer.first_name?.trim())
      defaults.firstName = customer.first_name.trim()
    if (lastName1) defaults.lastName1 = lastName1
    if (lastName2) defaults.lastName2 = lastName2

    if (birthDate) defaults.birthDate = birthDate

    const phone = (customer.mobile ?? customer.phone)?.trim()
    if (phone) defaults.phoneNumber = phone
    if (customer.email?.trim()) defaults.email = customer.email.trim()

    // --- Residence address (from customer.address) ---
    if (address) {
      if (address.street?.trim())
        defaults.wayDescription = address.street.trim()
      if (address.street2?.trim())
        defaults.additionalAddress = address.street2.trim()
      if (address.zip?.trim()) defaults.zipCode = address.zip.trim()
      if (address.city?.trim()) defaults.town = address.city.trim()
      if (address.country_code?.trim())
        defaults.country = address.country_code.trim().toUpperCase()
    }

    return Object.keys(defaults).length > 0 ? defaults : undefined
  }, [customer])
}

export default useCofidisApplicationFormDefaults
